import type { MaybeRefOrGetter } from 'vue';
import type { FieldSelection, GlobalCollectionId, GlobalFieldId, ResolvedMonospaceCollection } from '#layers/data-model/public';
import { computed, toValue } from 'vue';
import { usePermissionsStore } from '#layers/access/stores/permissions';
import { filterByPermissions } from '#layers/access/utils/permissions/filter-field-selection';
import { expandRequiredFields, getCollectionPrimaryKeyFields, getRelationPrimitiveFields } from '#layers/data-model/public';
import { useWorkspaceSchemaStore } from '#layers/workspace/public';

export interface UseProcessedFieldSelectionOptions {
	collection: MaybeRefOrGetter<ResolvedMonospaceCollection | undefined>;
	fields: MaybeRefOrGetter<FieldSelection | undefined>;
}

function resolveCollectionField(resolveCollection: (id: GlobalCollectionId) => ResolvedMonospaceCollection | undefined, collectionId: GlobalCollectionId, apiName: string) {
	const targetCol = resolveCollection(collectionId);
	if (!targetCol) return;

	const primitive = targetCol.primitiveFields?.find((f) => f.apiName === apiName);

	if (primitive) {
		return {
			id: primitive.id as GlobalFieldId,
			isRelation: false as const,
			isStudioOnly: !!primitive.isStudioOnly,
		};
	}

	const relation = targetCol.relationFields?.find((f) => f.apiName === apiName);

	if (relation) {
		return {
			id: relation.id as GlobalFieldId,
			isRelation: true as const,
			oppositeCollectionId: relation.oppositeCollectionId as GlobalCollectionId,
			isStudioOnly: !!relation.isStudioOnly,
		};
	}
}

export function useProcessedFieldSelection(options: UseProcessedFieldSelectionOptions) {
	const { findCollectionById } = useWorkspaceSchemaStore();
	const permissionsStore = usePermissionsStore();

	const processed = computed(() => {
		const col = toValue(options.collection);
		const rawFields = toValue(options.fields);
		if (!col || !rawFields) return { fields: undefined, excluded: [] as string[] };

		const resolveCollection = (id: GlobalCollectionId) =>
			id === col.id ? col : findCollectionById(id);

		const resolveField = (collectionId: GlobalCollectionId, apiName: string) =>
			resolveCollectionField(resolveCollection, collectionId, apiName);

		const { filtered, excluded } = filterByPermissions({
			collectionId: col.id,
			selection: rawFields,
			canReadField: (collectionId, fieldId) => permissionsStore.canReadField(collectionId, fieldId),
			canRead: (collectionId) => permissionsStore.canRead(collectionId),
			isAdmin: permissionsStore.isAdmin,
			resolveField,
		});

		const fields = expandRequiredFields({
			collectionId: col.id,
			selection: filtered,
			resolveField,
			getCollectionKeyFieldNames: (collectionId) => {
				const targetCol = resolveCollection(collectionId);
				if (!targetCol) return [];
				return getCollectionPrimaryKeyFields(targetCol).map((f) => f.apiName);
			},
			getRelationJoinFields: (collectionId, relationApiName) => {
				const targetCol = resolveCollection(collectionId);
				const relation = targetCol?.relationFields?.find((f) => f.apiName === relationApiName);
				if (!relation) return { ownFields: [], oppositeFields: [] };
				const { fields, oppositeFields } = getRelationPrimitiveFields(relation);
				return {
					ownFields: fields.map((f) => f.apiName),
					oppositeFields: oppositeFields.map((f) => f.apiName),
				};
			},
		});

		return { fields, excluded };
	});

	return {
		fields: computed(() => processed.value.fields),
		excluded: computed(() => processed.value.excluded),
	};
}
